import React, { useState } from 'react';
import { Box, Dialog, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { makeStyles } from '@mui/styles';
import { QuotationFlightImage } from '../../types';
import useBreakpoints from 'features/common/hooks/useBreakpoints';

interface FlightImagePreviewListProps {
	flightImages: QuotationFlightImage[];
}

const useStyles = makeStyles((theme) => ({
	imageContainer: {
		padding: theme.spacing(1),
	},
	image: {
		width: '100%',
		height: 'auto',
		display: 'block',
	},
}));

/**
 * Itinerarios cargados como imagen (captura del GDS). En mobile la miniatura
 * es ilegible, así que cada imagen se puede ampliar en un visor a pantalla
 * completa con zoom y cierre.
 */
const FlightImagePreviewList: React.FC<FlightImagePreviewListProps> = ({
	flightImages,
}: FlightImagePreviewListProps) => {
	const classes = useStyles();
	const { smallDown } = useBreakpoints();

	const [openImageIndex, setOpenImageIndex] = useState<number | null>(null);
	const [zoomed, setZoomed] = useState(false);

	const styles = {
		minWidth: smallDown ? '100vw' : '640px',
	};

	const handleOpen = (index: number) => {
		setZoomed(false);
		setOpenImageIndex(index);
	};
	const handleClose = () => setOpenImageIndex(null);

	const openImage = openImageIndex != null ? flightImages[openImageIndex] : null;

	return (
		<>
			<Box display="flex" flexDirection="column" gap={1}>
				{flightImages.map((image, index) => (
					<Box key={index}>
						<Paper
							elevation={3}
							className={classes.imageContainer}
							sx={{ ...styles, cursor: 'zoom-in' }}
							onClick={() => handleOpen(index)}
						>
							<img src={image.image} alt={`Itinerario de vuelo ${index + 1}`} className={classes.image} />
							<Typography
								variant="caption"
								display="block"
								textAlign="center"
								color="text.secondary"
								fontWeight={700}
								sx={{ pt: 0.5 }}
							>
								Tocá la imagen para ampliarla
							</Typography>
						</Paper>
					</Box>
				))}
			</Box>

			<Dialog
				fullScreen
				open={openImage != null}
				onClose={handleClose}
				PaperProps={{ sx: { backgroundColor: 'rgba(18, 10, 32, 0.96)' } }}
			>
				{/* Controles fijos, siempre visibles por encima de la imagen. */}
				<Box
					sx={{
						position: 'fixed',
						top: 'calc(env(safe-area-inset-top) + 8px)',
						right: 8,
						zIndex: 2,
						display: 'flex',
						gap: 1,
					}}
				>
					<IconButton
						onClick={() => setZoomed((value) => !value)}
						aria-label={zoomed ? 'Alejar imagen' : 'Acercar imagen'}
						sx={{ backgroundColor: 'rgba(255,255,255,0.14)', color: 'white' }}
					>
						{zoomed ? <ZoomOutIcon /> : <ZoomInIcon />}
					</IconButton>
					<IconButton
						onClick={handleClose}
						aria-label="Cerrar imagen"
						sx={{ backgroundColor: 'rgba(255,255,255,0.14)', color: 'white' }}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Contenedor scrolleable: con zoom la imagen se recorre con el dedo. */}
				<Box
					onClick={(event) => {
						// Tocar el fondo (no la imagen) también cierra.
						if (event.target === event.currentTarget) handleClose();
					}}
					sx={{
						flex: 1,
						overflow: 'auto',
						display: 'flex',
						alignItems: 'flex-start',
						WebkitOverflowScrolling: 'touch',
					}}
				>
					{openImage && (
						<img
							src={openImage.image}
							alt="Itinerario de vuelo ampliado"
							onClick={() => setZoomed((value) => !value)}
							style={{
								// El zoom va por minWidth: `width` en un flex item replaced se
								// clampea al contenedor en Chromium aunque flexShrink sea 0;
								// min-width es lo único que lo fuerza a desbordar y scrollear.
								width: '100%',
								minWidth: zoomed ? '190%' : undefined,
								maxWidth: zoomed ? 'none' : '100%',
								flexShrink: 0,
								height: 'auto',
								margin: 'auto',
								cursor: zoomed ? 'zoom-out' : 'zoom-in',
							}}
						/>
					)}
				</Box>
			</Dialog>
		</>
	);
};

export default FlightImagePreviewList;
